import { playCloseDialogSound, playOpenDialogSound } from '@wg/web2clientapi/sound';

import preloaded, { urls } from '~/preloaded';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { post } from '~/helpers/api';
import { showMonumentAchievementsDialog } from '~/helpers/dialogs';
import { getSoundPositions, layersOrder, OWN_BASE_ACTIONS, sendBuildingUpgradeNotification } from '~/helpers/supply';
import { syncAccountInfoThunk } from '~/store/thunks/accountThunks';

import { sendErrorNotification } from '~/web2ClientAPI/base';
import { ClanBaseSoundObjectPos } from '~/web2ClientAPI/supply';

import { fetchClan } from '~/Actions/ActionClanProfile';
import { actionsSupply } from '~/Actions/ActionSupply';

import type { IBuildings, IBuildingClickAction, BUILDINGS } from '~/Actions/ActionSupply';
import type { AppThunk, AppAsyncThunk } from '~/store';
import type { ISupplyBuildings } from '~/store/slices/settingsSlice';
import type { IApiBaseBuildingUpgradeResponse } from '~/types/api';
import type { Nullable } from '~/types/generics';

export const updateSupplyDataThunk =
  (buildings: IBuildings): AppThunk =>
  (dispatch) => {
    const settings = preloaded.settings || null;
    const currentAccount = preloaded.currentAccount || null;
    const isInGame = window.navigator.userAgent.includes(settings.ingameUserAgent);

    if (settings && currentAccount) {
      const supplyPreloaded = settings.supply;
      const tilingServiceHost = supplyPreloaded.tilingServiceHost || '';
      const tilesLocalPath = supplyPreloaded.tilesLocalPath || '';
      const tilesLocalExcludes = supplyPreloaded.tilesLocalExcludes || [];
      const tileUrlTemplate = supplyPreloaded.tileUrlTemplate || '';
      const maxZoom = supplyPreloaded.maxZoom || 7;
      const minZoom = supplyPreloaded.minZoom || 5;

      if (supplyPreloaded) {
        const allBuildings = supplyPreloaded.buildings;
        const newBuildings: Partial<ISupplyBuildings> = {};

        if (allBuildings?.length) {
          allBuildings.forEach((building) => {
            const markerPosition = building.levels[0]?.markerPosition || null;
            const buildingType = building.type;

            const alwaysLoadFromCDN = tilesLocalExcludes.includes(buildingType);
            const loadFromLocal = isInGame && !alwaysLoadFromCDN;

            const levels = Object.entries(building.levels).reduce((prev, [level_num, level_value]) => {
              const hostUrl = loadFromLocal ? tilesLocalPath : tilingServiceHost;
              const url = `${hostUrl}${level_value.url || ''}`;
              prev[level_num] = { ...level_value, url };
              return prev;
            }, {});

            newBuildings[building.name] = {
              ...building,
              level: buildings[building.name]?.level || 0,
              levels,
              markerPosition: markerPosition || [],
              modifiers: buildings[building.name]?.modifiers || [],
              order: layersOrder[building.name] || 0,
              sort: buildings[building.name]?.sort || 0,
              type: building.type,
            };
          });
        }

        const payload = {
          buildings: <ISupplyBuildings>newBuildings,
          tilingServiceHost: isInGame ? tilesLocalPath : tilingServiceHost,
          tileUrlTemplate,
          maxZoom,
          minZoom,
        };

        dispatch(actionsSupply.updateSupplyData(payload));

        // Init sounds
        ClanBaseSoundObjectPos(getSoundPositions(<ISupplyBuildings>newBuildings));
      }
    }
  };

export const setSelectedBuildingThunk =
  (buildingId: Nullable<BUILDINGS>): AppThunk =>
  (dispatch, getState) => {
    const state = getState();

    if (!buildingId) {
      void playCloseDialogSound();

      dispatch(actionsSupply.setMapBlur(false));
      dispatch(actionsSupply.setSelectedBuilding(null));
      dispatch(closeSupplySidebarThunk());
      return;
    }

    if (buildingId === state.ReducerSupply.selectedBuildingId) {
      return;
    }

    void playOpenDialogSound();

    dispatch(actionsSupply.setMapBlur(true));
    dispatch(actionsSupply.setSelectedBuilding(buildingId));
    dispatch(actionsSupply.openSupplySidebar());
  };

export const increaseBuildingLevelThunk =
  (buildingId: BUILDINGS, waitForSync = false): AppAsyncThunk =>
  async (dispatch, getState) => {
    const state = getState();

    const clanId = state.currentAccount.clanId;
    const buildings = state.ReducerSupply.buildings;

    if (!clanId || !buildings?.[buildingId]) {
      return;
    }

    const newLevel = buildings[buildingId].level + 1;
    if (newLevel > buildings[buildingId].maxLevel) {
      return;
    }

    dispatch(actionsSupply.setBuildingInProgress(buildingId));

    try {
      const url = urls.upgradeBuilding.replace('{clan_id}', `${clanId}`);

      const result = await post<IApiBaseBuildingUpgradeResponse>(url, {
        type: buildingId,
        level: newLevel,
      });

      if (result?.building?.level === undefined) {
        throw new Error();
      }

      dispatch(setBuildingLevelThunk(buildingId, result.building.level));
      sendBuildingUpgradeNotification(buildings, buildingId);

      if (waitForSync) {
        await dispatch(syncAccountInfoThunk());
        await dispatch(fetchClan(clanId));
      } else {
        void dispatch(syncAccountInfoThunk());
        void dispatch(fetchClan(clanId));
      }
    } catch (error: unknown) {
      dispatch(actionsSupply.upgradeBuildingError());
      sendErrorNotification();
    }
  };

const setBuildingLevelThunk =
  (buildingId: BUILDINGS, level: number): AppThunk =>
  (dispatch) => {
    dispatch(actionsSupply.setBuildingLevel(buildingId, level));
    dispatch(actionsSupply.hideBuildingConfirm());
  };

export const closeSupplySidebarThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  dispatch(actionsSupply.closeSupplySidebar());

  if (state.ReducerSupply.selectedBuildingId) {
    dispatch(setSelectedBuildingThunk(null));
    dispatch(actionsSupply.hideBuildingConfirm());
  }
};

export const selectBuildingThunk =
  (buildingId: Nullable<BUILDINGS>, clanId: number): AppThunk =>
  (dispatch, getState) => {
    const state = getState();

    const isMyClan = state.currentAccount.clanId === clanId;
    if (buildingId && isMyClan) {
      dwhExport.push(DWH_EVENTS.SUPPLY.VIEW_BUILDING, {
        building_id: buildingId,
      });
    }

    const currentBuilding = (buildingId && state.ReducerSupply.buildings?.[buildingId]) || null;
    if (!currentBuilding) {
      return;
    }

    const currentBuildingAction = currentBuilding.levels[currentBuilding.level || 0].action;
    if (!currentBuildingAction) {
      dispatch(setSelectedBuildingThunk(buildingId));
      return;
    }

    if (!OWN_BASE_ACTIONS.includes(currentBuildingAction)) {
      dispatch(buildingClickActionThunk(currentBuildingAction, clanId));
      return;
    }

    if (isMyClan) {
      dispatch(buildingClickActionThunk(currentBuildingAction, clanId));
      return;
    }

    dispatch(setSelectedBuildingThunk(buildingId));
  };

const buildingClickActionThunk =
  (action: IBuildingClickAction, clanId: number): AppThunk =>
  (dispatch) => {
    switch (action) {
      case 'show_achievements': {
        dispatch(showMonumentAchievementsDialog(clanId));
        break;
      }
      default: {
        break;
      }
    }
  };
